import {
  type GetCatalogProductDetailPageOption_pdp_option_info as OptionInfo,
  type GetCatalogProductDetailPageOption_pdp_option_info_catalog_product as OptionProduct,
  OptionItemList,
  PdpCatalogItemDisplayStatus,
  PdpCatalogSalesStatus,
  PdpDeliveryType,
} from '../graphql/select-option_types.2';
import type {
  CustomInputOptionList,
  CustomInputState,
  ItemListMapValueType,
  MatchedItemList,
  ProductOptionConfig,
  UnionItemListType,
  ZigzinItemList,
} from '../type/common.type';
import type { ProductOptionState } from '../type/common.type';
import { hasRequiredFieldInCustomInputOptions } from '../util/checkers/custom-input-field-checker';

// 옵션 초기화 상태 빌드
export function buildInitialState(option_info?: OptionInfo, config?: ProductOptionConfig): ProductOptionState {
  // 데이터 재정의 및 초기화
  if (option_info) {
    updateOptionItemNames(option_info);
    overrideBogoInfo(option_info, config);
  }

  const option_count = option_info?.catalog_product.product_option_list.length ?? 0;
  const bogo_required_quantity = option_info?.catalog_product.promotion_info?.bogo_info?.required_quantity ?? 0;

  const new_dropdown_states = Array.from({ length: bogo_required_quantity + 1 }, () =>
    Array.from({ length: option_count }, () => false),
  );

  const enabled_first_dropdown_open = !hasRequiredFieldInCustomInputOptions(
    option_info?.catalog_product.custom_input_option_list ?? [],
  );

  if (new_dropdown_states.length > 0 && new_dropdown_states[0].length > 0) {
    // 커스텀 텍스트 옵션이 있는 경우 첫 번째 드롭다운 열림 방지
    new_dropdown_states[0][0] = enabled_first_dropdown_open;
  }

  // Custom input states 초기화
  const custom_input_states = new Map<string, CustomInputState>();

  if (option_info?.catalog_product.custom_input_option_list) {
    sortCustomInputOptionsByRequired(option_info.catalog_product.custom_input_option_list).forEach((option) => {
      custom_input_states.set(option.name, {
        value: '',
        focused: false,
        inputEl: { current: null },
        shadowEl: { current: null },
        isRequired: option.is_required,
      });
    });
  }

  return {
    ui: {
      is_only_zigzin_option_active: config?.enabledShowOnlyZigzinOption ?? false,
      is_item_card_mode_active: false,
      is_restock_selection_mode: false,
      display_shipping_filter_button: option_info?.catalog_product.is_only_zigzin_button_visible ?? false,
      shipping_filter_color_type: hasStoreZigzinInMatchedItemList(option_info?.catalog_product)
        ? 'blue400'
        : 'purple400',
      restock_notification_start_index: null,
      restock_notification_status: null,
    },
    option: {
      info: option_info ?? null,
      map: buildOptionMap(option_info?.catalog_product),
    },
    enabled_first_dropdown_open,
    dropdown_states: new_dropdown_states,
    additional_dropdown_state: false,
    selection: {
      selected_group_index: 0,
      selected_dropdown_index: 0,
      metadata: {
        selected_option_name_list: [],
        selected_zigzin_option_id_list: [],
        selected_general_option_id_list: [],
        last_selected_shipping_type: null,
        previous_group_selections: [],
      },
    },
    custom_input_states,
    item_cards: { list: [], total_count: 0 },
  } as const;
}

// =========================
// Helper Functions
// =========================

// 옵션 조합 목록에서 스토어 직진 옵션 존재 여부 확인
const hasStoreZigzinInMatchedItemList = (option?: OptionProduct): boolean => {
  return option?.matched_item_list.some((item) => item.delivery_type === PdpDeliveryType.VENDOR_FLEX) ?? false;
};

// 커스텀 텍스트 옵션 필수 여부 정렬
const sortCustomInputOptionsByRequired = (customInputOptions: CustomInputOptionList[]) => {
  return customInputOptions.sort((cur, next) => Number(cur.is_required) - Number(next.is_required));
};

// item_attribute_list.value랑 순서가 다르게 정의된 item_attribute_list.name이 있어 재정의
const updateOptionItemNames = (options: OptionInfo) => {
  const updateItemNames = (items: ZigzinItemList[] | MatchedItemList[]) => {
    if (!Array.isArray(items) || items.length === 0) return;
    items.forEach((item) => {
      item.name =
        item.item_attribute_list
          ?.map((attr) => attr.value)
          .filter(Boolean)
          .join('/') ?? '';
    });
  };

  updateItemNames(options.catalog_product.matched_item_list);
  updateItemNames(options.catalog_product.zigzin_item_list);
};

// BOGO 프로모션 정보 덮어쓰기
const overrideBogoInfo = (option_info: OptionInfo, config?: ProductOptionConfig) => {
  if (config?.bogo_promotion_info) {
    option_info.catalog_product.promotion_info = {
      promotion_id: null,
      promotion_type: null,
      ...option_info.catalog_product.promotion_info,
      bogo_info: config.bogo_promotion_info,
    };
  }
};

const buildOptionMap = (option?: OptionProduct) => {
  if (!option) {
    return {
      path_index_map: { zigzin: new Map(), matched: new Map() },
      item_list_map: { zigzin: new Map(), matched: new Map() },
    };
  }

  const item_list_map = {
    zigzin: createItemListMap(option.zigzin_item_list),
    matched: createItemListMap(option.matched_item_list),
  };

  const path_index_map = {
    zigzin: createPathIndexMap(option.zigzin_item_list),
    matched: createPathIndexMap(option.matched_item_list),
  };

  return { item_list_map, path_index_map };
};

/**
 * 아이템 목록을 경로별로 인덱싱하는 헬퍼 함수
 * sales_status가 ON_SALE인 항목을 우선적으로 정렬
 * delivery_type 우선순위: ZIGZIN > FAST > GENERAL > VENDOR_FLEX
 * @param item_list - 전체 아이템 목록
 * @returns 경로별 아이템 인덱스 맵
 */
const createPathIndexMap = (item_list: UnionItemListType[]): Map<string, UnionItemListType> => {
  // delivery_type 우선순위 정의
  const getDeliveryTypePriority = (delivery_type: PdpDeliveryType | null): number => {
    switch (delivery_type) {
      case PdpDeliveryType.ZIGZIN:
      case PdpDeliveryType.VENDOR_FLEX:
        return 0; // 가장 높은 우선순위
      case PdpDeliveryType.FAST:
        return 1;
      case PdpDeliveryType.GENERAL:
        return 2;
      default:
        return 3; // null인 경우 가장 낮은 우선순위
    }
  };

  // sales_status와 delivery_type 우선순위에 따라 정렬
  const sorted_item_list = [...item_list].sort((a, b) => {
    // 1. sales_status 우선순위 비교 (ON_SALE이 최우선)
    const a_is_on_sale = a.sales_status === PdpCatalogSalesStatus.ON_SALE;
    const b_is_on_sale = b.sales_status === PdpCatalogSalesStatus.ON_SALE;

    if (a_is_on_sale && !b_is_on_sale) {
      return -1;
    }
    if (!a_is_on_sale && b_is_on_sale) {
      return 1;
    }

    // 2. sales_status가 같다면 delivery_type 우선순위로 비교
    const a_delivery_priority = getDeliveryTypePriority(a.delivery_type);
    const b_delivery_priority = getDeliveryTypePriority(b.delivery_type);

    return a_delivery_priority - b_delivery_priority;
  });

  // 공간 복잡도: O(n × k) - 최대 n × k개의 엔트리를 저장할 Map
  const path_index_map = new Map<string, UnionItemListType>();
  // 외부 루프: n번 반복
  for (const item of sorted_item_list) {
    // 공간 복잡도: O(k) - 경로를 '/'로 분할한 배열
    const path_parts = item.name.split('/');
    // 공간 복잡도: O(평균 경로 길이) - 누적 경로 문자열
    let cumulative_path = '';
    // 내부 루프: 평균 k번 반복
    for (let i = 0; i < path_parts.length; i++) {
      cumulative_path += (i > 0 ? '/' : '') + path_parts[i];

      // 중복 확인 후 Map에 저장 - 공간 복잡도에 기여
      if (!path_index_map.has(cumulative_path)) {
        path_index_map.set(cumulative_path, item);
      }
    }
  }

  return path_index_map;
};

/**
 * 아이템 목록을 맵으로 변환하는 함수
 * @param item_list - 아이템 목록
 * @returns 아이템 목록 맵
 */
const createItemListMap = (item_list: OptionItemList[]): Map<string, ItemListMapValueType> => {
  if (!Array.isArray(item_list) || item_list.length === 0) {
    return new Map(); // 공간 복잡도: O(1) - 빈 Map
  }
  const DELIMITER = ';';
  // 공간 복잡도: O(n) - 최대 2n개 엔트리를 저장할 Map
  const itemListMap = new Map<string, ItemListMapValueType>();
  const itemListLength = item_list.length; // 공간 복잡도: O(1)

  for (let i = 0; i < itemListLength; i++) {
    const item = item_list[i];
    const attrList = item.item_attribute_list;

    // 유효성 검사
    if (!Array.isArray(attrList) || attrList.length === 0) {
      console.warn(`Invalid item_attribute_list for item ${item.id}`);
      continue;
    }

    // 속성 ID 처리 - 문자열 연결 최적화
    // 공간 복잡도: O(m) - 문자열 길이는 attribute 개수에 비례
    let id = attrList[0].value_id;
    const firstId = id; // 공간 복잡도: O(1)
    for (let j = 1; j < attrList.length; j++) {
      id += DELIMITER + attrList[j].value_id;
    }

    // 현재 아이템 값 생성 - 공간 복잡도: O(1)
    const currentItem = createItemValue(item);

    // ID로 현재 아이템 저장 - Map에 엔트리 추가
    itemListMap.set(id, currentItem);

    /**
     * 첫번째 조건에 대한 상태 판별 조건을 미리 저장해둔다.
     */
    const firstMatchedItem = itemListMap.get(firstId);
    if (firstMatchedItem) {
      const displayStatusChanged = updateItemStatus(
        currentItem,
        firstMatchedItem,
        'display_status',
        PdpCatalogItemDisplayStatus.VISIBLE,
      );
      const salesStatusChanged = updateItemStatus(
        currentItem,
        firstMatchedItem,
        'sales_status',
        PdpCatalogSalesStatus.ON_SALE,
      );
      const delivertTypeStatusChanged = updateItemStatus(
        currentItem,
        firstMatchedItem,
        'delivery_type',
        PdpDeliveryType.FAST,
      );

      if (displayStatusChanged || salesStatusChanged || delivertTypeStatusChanged) {
        itemListMap.set(firstId, currentItem);
      }
    } else {
      itemListMap.set(firstId, currentItem);
    }
  }
  return itemListMap;
};

const createItemValue = (item: OptionItemList): ItemListMapValueType => ({
  id: item.id,
  name: item.name,
  display_status: item.display_status,
  sales_status: item.sales_status,
  wms_notification_info: item.wms_notification_info,
  final_price: item.final_price,
  remain_stock: item.remain_stock,
  delivery_type: item.delivery_type,
  expected_delivery_date: item.expected_delivery_date,
  expected_delivery_time: item.expected_delivery_time,
  price: item.price,
  item_attribute_list: item.item_attribute_list,
  option_type: item.option_type,
  discount_info: item.discount_info,
  price_delta: item.price_delta,
  item_code: item.item_code,
  is_zigzin: item.is_zigzin,
});

const updateItemStatus = (
  currentItem: ItemListMapValueType,
  matchedItem: ItemListMapValueType,
  statusKey: 'display_status' | 'sales_status' | 'delivery_type',
  statusVisible: PdpCatalogItemDisplayStatus | PdpCatalogSalesStatus | PdpDeliveryType,
) => {
  if (matchedItem[statusKey] !== statusVisible && currentItem[statusKey] === statusVisible) {
    return true;
  }
  return false;
};
